import powerDialerRingSound from '@/assets/ringtones/power-dialer-ring.mp3';
import { useUserBusyModeMutation } from '@/hooks/api/use-user-busy-mode-mutation';
import { useTranslation } from '@/hooks/use-translation';
import { parsePhoneNumberData } from '@/lib/phone-number';
import { useAppCallActions } from '@/providers/app-call-actions/use-app-call-actions';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { $apiAllo } from '@/services/api-allo/client';
import { $apiAlloRemote, $fetchAlloRemote } from '@/services/api-allo/client-remote';
import {
  DIALING_SESSION_CLIENT_ID,
  onDialingSessionClaimed,
} from '@/services/dialing-session-claim';
import type { Line } from '@/types/calls-types';
import { useHasPowerDialerApiKey } from '@/views/power-dialer/hooks/use-has-power-dialer-api-key';
import { usePowerDialerContacts } from '@/views/power-dialer/hooks/use-power-dialer-contacts';
import {
  type DialingState,
  type EndedSessionContext,
  type SessionContact,
  getSessionContactKey,
} from '@/views/power-dialer/types';
import type { VoiceCallStateStatus } from '@packages/client-sdk-react/src/voice/voice-call-container';
import { useVonageClient } from '@packages/client-sdk-react/src/vonage-client-provider';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { type QueueDoNotDisturb, resolveQueueDoNotDisturb } from '../lib/queue-do-not-disturb';
import { setPowerDialerBusyModeBannerHidden } from './power-dialer-busy-mode-store';
import {
  INITIAL_STATE,
  type PowerDialerState,
  makeAttemptId,
  powerDialerReducer,
} from './power-dialer-reducer';

type DialingQueueData = {
  id?: string;
  doNotDisturb?: QueueDoNotDisturb;
  do_not_disturb?: QueueDoNotDisturb;
};

type PowerDialerBusyModeSession = {
  sessionId: string;
  doNotDisturb: QueueDoNotDisturb;
  enableRequested: boolean;
  enabledByPowerDialer: boolean;
  disableRequested: boolean;
  enablePromise?: Promise<unknown>;
};

type ActivePowerDialerContextValue = {
  // Public, derived state — stable shape consumers read from today.
  activeSessionId: string | null;
  // Historical name; value is the active queue item key so duplicate contacts are distinct.
  activeContactId: string | undefined;
  currentIndex: number;
  isPaused: boolean;
  stateOverrides: Record<string, DialingState>;
  outboundLine: Line | null;

  // Public actions.
  startSession: (line: Line) => Promise<void>;
  /** Claim a still-open session (started here or on another client) for this
   *  app instance. Resolves false when the takeover failed — callers gate the
   *  countdown / `resumeSession` on it. */
  claimSession: (sessionId: string) => Promise<boolean>;
  /** Start the back-to-back dialing loop against an already-claimed session. */
  resumeSession: (sessionId: string, line: Line) => void;
  endSession: () => void;
  pause: () => void;
  resume: () => void;
  /** Advance past the current contact. Hangs up if there's a live call;
   *  finalizes the row as no-answer and moves on if there isn't. */
  skip: () => void;
  goToIndex: (index: number) => void;

  // Full reducer state (for consumers that need phase, inFlight, etc.).
  state: PowerDialerState;

  // End-of-session summary context.
  lastEndedSession: EndedSessionContext | null;
  clearLastEndedSession: () => void;
};

const ActivePowerDialerContext = createContext<ActivePowerDialerContextValue | null>(null);

type Props = { children: ReactNode };

/**
 * Single source of truth for a running Power Dialer session. The reducer in
 * `power-dialer-reducer.ts` owns lifecycle state (phase, cursor, in-flight
 * binding, per-contact dialing state). This component hosts the effects that
 * place real calls and mirror the SDK `callStatus` back into the reducer.
 *
 * Mounted at `_app` layout level in `src/routes/_app/route.tsx` so the session
 * survives route changes.
 */
export const ActivePowerDialerProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(powerDialerReducer, INITIAL_STATE);
  const sessionStartedAtRef = useRef<string | null>(null);
  // Stable archive of every contact that has been dialed in the current
  // session — survives queue mutations that prune called contacts from the
  // reducer's live `contacts` array, so the end-of-session summary can render
  // every dial regardless of whether the queue API still surfaces it.
  const dialedContactsByKeyRef = useRef<Record<string, SessionContact>>({});
  const [lastEndedSession, setLastEndedSession] = useState<EndedSessionContext | null>(null);
  const clearLastEndedSession = useCallback(() => setLastEndedSession(null), []);

  // ─── Contact source ────────────────────────────────────────────────────
  //
  // The queue comes from `GET /v1/dialing-queues/current` via the API key the
  // user configured on the Power Dialer page. `startSession` snapshots the
  // live list; the QUEUE_MUTATED sync effect below keeps the reducer's
  // snapshot in step with the live list between renders.
  const { contacts: liveContacts } = usePowerDialerContacts();
  const callableLiveContacts = useMemo(
    () => liveContacts.filter((contact) => !contact.call_id),
    [liveContacts],
  );
  const { hasApiKey: hasPowerDialerApiKey } = useHasPowerDialerApiKey();
  const { data: dialingQueueData } = $apiAlloRemote.useQuery(
    'get',
    '/v1/dialing-queues/current',
    {},
    { enabled: hasPowerDialerApiKey, refetchOnWindowFocus: 'always' },
  );
  const liveQueue = (dialingQueueData as { data?: DialingQueueData } | undefined)?.data;
  const liveQueueId = liveQueue?.id ?? null;
  // Honor the queue's `do_not_disturb` column from the API: a session forces
  // the user into busy mode only when the queue asks for it (see
  // `resolveQueueDoNotDisturb` for the casing/default handling).
  const liveQueueDoNotDisturb = resolveQueueDoNotDisturb(liveQueue);

  // ─── Side-effect APIs ──────────────────────────────────────────────────
  const voiceActions = useAppCallActions();
  const { call, callStatus, direction, hangupCall } = voiceActions;
  const vonageClient = useVonageClient();
  const queryClient = useQueryClient();

  // React Query's `refetchOnWindowFocus` is driven by `visibilitychange`,
  // which doesn't fire when the user alt-tabs to another app/window — the
  // page is still "visible" behind it — so the queue goes stale when it's
  // mutated from outside (other window, API, teammate push). Bridge the
  // `focus` event to a scoped invalidation so the list stays live without
  // touching the rest of the app's caches.
  const queueQueryKey = useMemo(
    () => $apiAlloRemote.queryOptions('get', '/v1/dialing-queues/current', {}).queryKey,
    [],
  );
  const usageQueryKey = useMemo(
    () => $apiAlloRemote.queryOptions('get', '/v1/billing/usage').queryKey,
    [],
  );
  const currentSessionQueryKey = useMemo(
    () => $apiAlloRemote.queryOptions('get', '/v1/dialing-queues/sessions/current', {}).queryKey,
    [],
  );
  useEffect(() => {
    const handleFocus = () => {
      void queryClient.invalidateQueries({ queryKey: queueQueryKey });
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [queryClient, queueQueryKey]);

  const { t } = useTranslation('desktop_ui');
  const { data: userData } = $apiAllo.useQuery('get', '/v1/user', {});
  const { mutateAsync: setUserBusyMode } = useUserBusyModeMutation({ showSuccessToast: false });
  const userId = userData?.data?.id;
  const userMode = userData?.data?.config?.mode;
  const powerDialerBusyModeSessionRef = useRef<PowerDialerBusyModeSession | null>(null);
  const pendingCallStartRef = useRef<{
    attemptId: string;
    controller: AbortController;
  } | null>(null);
  const [pendingCallStartAttemptId, setPendingCallStartAttemptId] = useState<string | null>(null);

  const abortPendingCallStart = useCallback((attemptId?: string) => {
    const pending = pendingCallStartRef.current;
    if (!pending) {
      return;
    }
    if (attemptId && pending.attemptId !== attemptId) {
      return;
    }
    pending.controller.abort();
  }, []);

  // ─── Public action callbacks ───────────────────────────────────────────
  const startSession = useCallback(
    async (line: Line) => {
      if (callableLiveContacts.length === 0) {
        return;
      }
      if (state.phase !== 'idle') {
        return;
      }
      // The generated type spells this `alloNumber`, but the API expects
      // snake_case `allo_number` on the wire (per CLAUDE.md: spec types may
      // differ from response/request body shape — prefer snake_case).
      const { data, error } = await $fetchAlloRemote.POST('/v1/dialing-queues/sessions', {
        body: { allo_number: line.phoneNumber.number.number } as unknown as {
          alloNumber: string;
        },
      });
      const sessionId = data?.data?.id;
      if (error || !sessionId) {
        toast.error(t('components.powerDialer.session.startFailedToast'));
        return;
      }
      powerDialerBusyModeSessionRef.current =
        liveQueueDoNotDisturb === 'ENABLED'
          ? {
              sessionId,
              doNotDisturb: liveQueueDoNotDisturb,
              enableRequested: false,
              enabledByPowerDialer: false,
              disableRequested: false,
            }
          : null;
      setPowerDialerBusyModeBannerHidden(false);
      sessionStartedAtRef.current = new Date().toISOString();
      dialedContactsByKeyRef.current = {};
      dispatch({ type: 'START_REQUESTED' });
      dispatch({
        type: 'START_SESSION',
        sessionId,
        queueId: liveQueueId,
        line,
        contacts: callableLiveContacts,
      });
      void queryClient.invalidateQueries({ queryKey: currentSessionQueryKey });
    },
    [
      callableLiveContacts,
      liveQueueId,
      liveQueueDoNotDisturb,
      state.phase,
      t,
      queryClient,
      currentSessionQueryKey,
    ],
  );

  const claimSession = useCallback(
    async (sessionId: string) => {
      // Same casing mismatch as `startSession`: the generated type spells this
      // `clientId`, but the API expects snake_case on the wire.
      const { error } = await $fetchAlloRemote.POST('/v1/dialing-queues/sessions/{id}/claim', {
        params: { path: { id: sessionId } },
        body: { client_id: DIALING_SESSION_CLIENT_ID } as unknown as { clientId: string },
      });
      if (error) {
        toast.error(t('components.powerDialer.session.resumeFailedToast'));
        // The session may have ended on another client — re-sync the button.
        void queryClient.invalidateQueries({ queryKey: currentSessionQueryKey });
        return false;
      }
      return true;
    },
    [t, queryClient, currentSessionQueryKey],
  );

  const resumeSession = useCallback(
    (sessionId: string, line: Line) => {
      if (callableLiveContacts.length === 0) {
        return;
      }
      if (state.phase !== 'idle') {
        return;
      }
      powerDialerBusyModeSessionRef.current =
        liveQueueDoNotDisturb === 'ENABLED'
          ? {
              sessionId,
              doNotDisturb: liveQueueDoNotDisturb,
              // The relinquishing client hands busy-mode ownership over without
              // restoring FREE (see the claim listener below). If the user is
              // already BUSY when we claim, adopt ownership so this client
              // restores FREE when the session finally ends.
              enableRequested: userMode === 'BUSY',
              enabledByPowerDialer: userMode === 'BUSY',
              disableRequested: false,
            }
          : null;
      setPowerDialerBusyModeBannerHidden(false);
      sessionStartedAtRef.current = new Date().toISOString();
      dialedContactsByKeyRef.current = {};
      dispatch({ type: 'START_REQUESTED' });
      dispatch({
        type: 'START_SESSION',
        sessionId,
        queueId: liveQueueId,
        line,
        contacts: callableLiveContacts,
      });
      void queryClient.invalidateQueries({ queryKey: currentSessionQueryKey });
    },
    [
      callableLiveContacts,
      liveQueueId,
      liveQueueDoNotDisturb,
      state.phase,
      userMode,
      queryClient,
      currentSessionQueryKey,
    ],
  );

  const endSession = useCallback(() => {
    abortPendingCallStart();
    dispatch({ type: 'END_REQUESTED' });
  }, [abortPendingCallStart]);

  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const resume = useCallback(() => dispatch({ type: 'RESUME' }), []);
  const skip = useCallback(() => {
    // Keep the session cursor pinned to the SDK call until the call is
    // actually idle. Advancing here would let the Power Dialer show contact
    // N+1 while the call bar is still ringing contact N.
    if (state.inFlight) {
      dispatch({ type: 'SKIP_REQUESTED' });
      abortPendingCallStart(state.inFlight.attemptId);
      void hangupCall().catch(() => undefined);
      return;
    }
    dispatch({ type: 'ADVANCE_REQUESTED' });
  }, [state.inFlight, abortPendingCallStart, hangupCall]);
  const goToIndex = useCallback((index: number) => {
    dispatch({ type: 'RESTART_FROM_INDEX', index });
  }, []);

  // ─── Effect: relinquish when another client claims the session ─────────
  // `DIALING_SESSION_CLAIMED` (via the notification handler) means another
  // tab/app instance took over the loop. Stop back-to-back dialing here
  // WITHOUT POSTing `/end` — the session stays alive on the claimer. Any live
  // call detaches and continues under the call bar, same as `endSession`.
  useEffect(() => {
    return onDialingSessionClaimed(({ sessionId, clientId }) => {
      if (state.phase !== 'dialing' && state.phase !== 'paused') {
        return;
      }
      if (!sessionId || sessionId !== state.sessionId) {
        return;
      }
      if (clientId === DIALING_SESSION_CLIENT_ID) {
        return;
      }
      // Hand busy-mode ownership to the claiming client: clearing the managed
      // ref stops the disable effect from flipping the user back to FREE while
      // the session keeps running elsewhere (`resumeSession` adopts it there).
      powerDialerBusyModeSessionRef.current = null;
      setPowerDialerBusyModeBannerHidden(false);
      abortPendingCallStart();
      dispatch({ type: 'SESSION_CLAIMED_ELSEWHERE', sessionId });
      toast.info(t('components.powerDialer.session.claimedElsewhereToast'));
    });
  }, [state.phase, state.sessionId, abortPendingCallStart, t]);

  // ─── Effect: Power Dialer-owned Do Not Disturb ─────────────────────────
  // Queues can request DND for the session. Only turn it back off if this
  // provider changed the user from FREE to BUSY; a user who was already BUSY
  // before the session keeps their manual state.
  useEffect(() => {
    const managedSession = powerDialerBusyModeSessionRef.current;
    if (
      !managedSession ||
      managedSession.sessionId !== state.sessionId ||
      managedSession.doNotDisturb !== 'ENABLED' ||
      managedSession.enableRequested ||
      (state.phase !== 'dialing' && state.phase !== 'paused')
    ) {
      return;
    }
    if (!userId) {
      return;
    }

    managedSession.enableRequested = true;
    if (userMode === 'BUSY') {
      return;
    }

    managedSession.enabledByPowerDialer = true;
    setPowerDialerBusyModeBannerHidden(true);
    const enablePromise = setUserBusyMode({ body: { mode: 'BUSY', userId } })
      .catch((error: unknown) => {
        const current = powerDialerBusyModeSessionRef.current;
        if (current?.sessionId === managedSession.sessionId) {
          current.enabledByPowerDialer = false;
          setPowerDialerBusyModeBannerHidden(false);
        }
        throw error;
      })
      .finally(() => {
        const current = powerDialerBusyModeSessionRef.current;
        if (current?.sessionId === managedSession.sessionId) {
          current.enablePromise = undefined;
        }
      });
    managedSession.enablePromise = enablePromise;
    void enablePromise.catch(() => undefined);
  }, [state.sessionId, state.phase, userId, userMode, setUserBusyMode]);

  useEffect(() => {
    const managedSession = powerDialerBusyModeSessionRef.current;
    if (
      !managedSession?.enabledByPowerDialer ||
      managedSession.disableRequested ||
      (state.sessionId === managedSession.sessionId &&
        (state.phase === 'dialing' || state.phase === 'paused'))
    ) {
      return;
    }
    if (!userId) {
      return;
    }

    managedSession.disableRequested = true;
    void (async () => {
      try {
        await managedSession.enablePromise;
      } catch {
        const current = powerDialerBusyModeSessionRef.current;
        if (current?.sessionId === managedSession.sessionId) {
          powerDialerBusyModeSessionRef.current = null;
          setPowerDialerBusyModeBannerHidden(false);
        }
        return;
      }

      const current = powerDialerBusyModeSessionRef.current;
      if (current?.sessionId !== managedSession.sessionId || !current.enabledByPowerDialer) {
        return;
      }

      try {
        await setUserBusyMode({ body: { mode: 'FREE', userId } });
      } finally {
        const latest = powerDialerBusyModeSessionRef.current;
        if (latest?.sessionId === managedSession.sessionId) {
          powerDialerBusyModeSessionRef.current = null;
          setPowerDialerBusyModeBannerHidden(false);
        }
      }
    })();
  }, [state.sessionId, state.phase, userId, setUserBusyMode]);

  // ─── Effect: keep reducer's contact snapshot in sync with the live source ─
  // Only while a session is running — no point mutating state otherwise.
  //
  // `externalContacts` comes from `usePowerDialerContacts`, which builds its
  // array via `useQueries(...)`. React Query's `useQueries` returns a fresh
  // array reference on every render (by design — it wraps live query state),
  // so a plain reference check would dispatch QUEUE_MUTATED every render and
  // trigger React's "Maximum update depth exceeded" infinite-loop guard.
  //
  // Content-check instead. The reducer only cares about the identity and
  // phone number of each contact at `currentIndex` — name/company/email
  // enrichment doesn't affect dialing logic, and the view reads those
  // straight from `usePowerDialerContacts` anyway.
  useEffect(() => {
    if (state.phase !== 'dialing' && state.phase !== 'paused') {
      return;
    }
    const current = state.contacts;
    const sameShape =
      callableLiveContacts.length === current.length &&
      callableLiveContacts.every(
        (c, i) =>
          getSessionContactKey(c) === (current[i] ? getSessionContactKey(current[i]) : undefined) &&
          c.numbers?.[0] === current[i]?.numbers?.[0],
      );
    if (sameShape) {
      return;
    }
    dispatch({
      type: 'QUEUE_MUTATED',
      queueId: liveQueueId ?? state.queueId,
      contacts: callableLiveContacts,
    });
  }, [callableLiveContacts, state.phase, state.contacts, state.queueId, liveQueueId]);

  // ─── Effect: archive every dialed contact for the summary ──────────────
  //
  // `state.contacts` shrinks as called contacts get a `call_id` and fall out
  // of `callableLiveContacts`, so by session end the reducer's contact list
  // typically holds only the not-yet-dialed remainder. The summary screen
  // needs a row per dial — so we mirror anything with a `dialingState` entry
  // into a stable ref. We pull from both sources: `state.contacts` catches
  // contacts at the moment of dialing; `liveContacts` (unfiltered) re-supplies
  // the same contact later with `routing_result` populated by the queue API.
  useEffect(() => {
    if (state.phase !== 'dialing' && state.phase !== 'paused') {
      return;
    }
    const archive = dialedContactsByKeyRef.current;
    const record = (contact: SessionContact) => {
      const key = getSessionContactKey(contact);
      if (state.dialingState[key]) {
        archive[key] = contact;
      }
    };
    for (const c of state.contacts) {
      record(c);
    }
    for (const c of liveContacts) {
      record(c);
    }
  }, [state.phase, state.contacts, state.dialingState, liveContacts]);

  // ─── Effect B: ingest live callStatus transitions ──────────────────────
  //
  // Declared BEFORE Effect A (the dialer) on purpose. When a callStatus
  // change renders, React runs effects in declaration order and batches
  // the dispatches they queue. If the dialer ran first and observed
  // `callStatus === 'idle'`, it would queue `DIAL_CONTACT` before this
  // effect queued the trailing `CALL_STATUS_CHANGED(* → idle)`. The
  // reducer would then apply them in that order: `DIAL_CONTACT` sets
  // `inFlight` to the NEXT contact, and the terminal-idle event from the
  // PREVIOUS call's hangup would be mis-attributed to it — finalizing and
  // advancing past a contact that was just dialed. Reversing the order
  // keeps status ingestion strictly ahead of the next dial, so Skip
  // advances the cursor exactly once and the next call starts cleanly.
  const prevStatusRef = useRef<VoiceCallStateStatus>(callStatus);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = callStatus;
    if (prev === callStatus) {
      return;
    }
    if (state.inFlight && prev !== 'idle' && callStatus === 'idle') {
      void queryClient.refetchQueries({
        queryKey: $apiAlloRemote.queryOptions('get', '/v1/dialing-queues/current', {}).queryKey,
      });
    }
    dispatch({ type: 'CALL_STATUS_CHANGED', prev, next: callStatus });
  }, [callStatus, queryClient, state.inFlight]);

  // If Skip was requested before the SDK exposed a call id, the first hangup
  // attempt can be a no-op. As soon as the call reaches a cancellable SDK
  // state, try again while leaving the reducer pinned to the same contact.
  const skipHangupAttemptRef = useRef<string | null>(null);
  useEffect(() => {
    const inFlight = state.inFlight;
    if (!inFlight?.skipRequested) {
      skipHangupAttemptRef.current = null;
      return;
    }
    if (callStatus === 'idle' || callStatus === 'hangingup') {
      return;
    }
    const attemptKey = `${inFlight.attemptId}:${callStatus}`;
    if (skipHangupAttemptRef.current === attemptKey) {
      return;
    }
    skipHangupAttemptRef.current = attemptKey;
    void hangupCall().catch(() => undefined);
  }, [state.inFlight, callStatus, hangupCall]);

  // ─── Effect A: dial the current contact ────────────────────────────────
  // Fires exactly once per cursor advance. Three guards:
  //   1. `state.inFlight` — don't clobber a live call. Cleared only after
  //      terminal call status (or a no-call fast path) so cursor and SDK
  //      state stay pinned to the same contact.
  //   2. `callStatus === 'idle'` — wait for the SDK to fully tear down the
  //      previous call before placing the next. Prevents the race where a
  //      late `* → idle` transition from call N gets attributed to call N+1.
  //   3. `dialingState[contactKey]` is unfinalized — once a row is marked
  //      connected/no-answer/voicemail, don't auto-redial it. User moves on
  //      explicitly via ADVANCE_REQUESTED (the Skip/Next button).
  useEffect(() => {
    if (state.phase !== 'dialing' || state.inFlight || !state.line) {
      return;
    }
    if (pendingCallStartAttemptId) {
      return;
    }
    if (callStatus !== 'idle') {
      return;
    }
    if (state.currentIndex >= state.contacts.length) {
      return;
    }
    const contact = state.contacts[state.currentIndex];
    if (!contact) {
      return;
    }
    const contactKey = getSessionContactKey(contact);
    const contactId = contact.id;
    if (!contactKey) {
      return;
    }
    const already = state.dialingState[contactKey];
    if (already === 'connected' || already === 'no-answer' || already === 'voicemail') {
      return;
    }
    const rawNumber = contact.numbers?.[0];
    if (!rawNumber) {
      dispatch({ type: 'DIAL_FAILED', contactKey });
      return;
    }
    const phoneData = parsePhoneNumberData(rawNumber);
    if (!phoneData) {
      dispatch({ type: 'DIAL_FAILED', contactKey });
      return;
    }
    const attemptId = makeAttemptId(
      state.sessionId ?? 'no-session',
      state.queueId,
      state.currentIndex,
      contactKey,
    );
    const controller = new AbortController();
    pendingCallStartRef.current = { attemptId, controller };
    setPendingCallStartAttemptId(attemptId);
    dispatch({ type: 'DIAL_CONTACT', contactKey, attemptId });
    // Optimistically bump the usage badge so the count grows the instant the
    // call is dispatched. The backend tallies the call asynchronously, so we
    // can't refetch right after dial — the focus handler reconciles instead.
    // NB: the remote OpenAPI spec types this field as camelCase, but the
    // backend serializes snake_case (`dialing_queue_calls`) — write the real
    // key so the bump matches what the read site and refetch see.
    queryClient.setQueryData<{ data?: { dialing_queue_calls?: number } }>(usageQueryKey, (old) => {
      if (!old?.data) {
        return old;
      }
      return {
        ...old,
        data: {
          ...old.data,
          dialing_queue_calls: (old.data.dialing_queue_calls ?? 0) + 1,
        },
      };
    });
    const callContext: { dialing_queue_id?: string; dialing_session_id?: string } = {};
    if (state.queueId) {
      callContext.dialing_queue_id = state.queueId;
    }
    if (state.sessionId) {
      callContext.dialing_session_id = state.sessionId;
    }
    const callContextOrUndefined = Object.keys(callContext).length > 0 ? callContext : undefined;
    // Each bulk-dialed contact fires its own `Call Started` so the
    // outbound-call funnel captures the Power Dialer volume alongside the
    // other entry points (sidebar_dialer, call_bar, phone_link, etc.).
    analytics.track(ANALYTICS_EVENTS.CALL.STARTED, { entry_point: 'power_dialer' });
    void call(state.line, phoneData, undefined, contactId, callContextOrUndefined, {
      signal: controller.signal,
    })
      .then((started) => {
        if (!started) {
          dispatch({ type: 'CALL_START_UNRECOVERABLE', contactKey, attemptId });
        }
      })
      .catch(() => {
        dispatch({ type: 'CALL_START_UNRECOVERABLE', contactKey, attemptId });
      })
      .finally(() => {
        if (pendingCallStartRef.current?.attemptId === attemptId) {
          pendingCallStartRef.current = null;
        }
        setPendingCallStartAttemptId((current) => (current === attemptId ? null : current));
      });
  }, [
    state.phase,
    state.inFlight,
    state.currentIndex,
    state.contacts,
    state.sessionId,
    state.queueId,
    state.line,
    state.dialingState,
    pendingCallStartAttemptId,
    callStatus,
    call,
    queryClient,
    usageQueryKey,
  ]);

  // ─── Effect: end-of-session teardown ───────────────────────────────────
  // When the reducer lands on `phase: 'ended'`, clean up session state
  // immediately. Any live call detaches from the session and continues under
  // the call bar — ending the session terminates the auto-dial loop, not the
  // call itself. We still abort any pre-call processing attempt that hasn't
  // placed a real call yet (the SDK has nothing to hang up, and letting it
  // complete would dial a contact the user just abandoned).
  useEffect(() => {
    if (state.phase !== 'ended') {
      return;
    }
    abortPendingCallStart();
    const lastIdx = state.contacts.length - 1;
    const lastContact = lastIdx >= 0 ? state.contacts[lastIdx] : undefined;
    const lastContactKey = lastContact ? getSessionContactKey(lastContact) : undefined;
    const lastFinalized =
      lastContactKey &&
      ['connected', 'no-answer', 'voicemail'].includes(state.dialingState[lastContactKey] ?? '');
    if (state.currentIndex === lastIdx && lastFinalized) {
      toast.success(t('components.powerDialer.session.completedToast'));
    }

    // Snapshot the session before the reducer resets, so the summary screen
    // can render outcome stats and build the CubeJS query.
    if (state.sessionId && state.line) {
      // Pull from the dialed-contacts archive — `state.contacts` only holds
      // not-yet-dialed remainders by this point.
      const dialedContacts = Object.values(dialedContactsByKeyRef.current);
      setLastEndedSession({
        session_id: state.sessionId,
        queue_id: state.queueId,
        started_at: sessionStartedAtRef.current ?? new Date().toISOString(),
        ended_at: new Date().toISOString(),
        line_name: state.line.lineName,
        line_number: state.line.phoneNumber.label,
        contacts: dialedContacts,
        dialing_state: state.dialingState,
      });
      sessionStartedAtRef.current = null;
      void $fetchAlloRemote
        .POST('/v1/dialing-queues/sessions/{id}/end', {
          params: { path: { id: state.sessionId } },
        })
        .finally(() => {
          // No open session anymore — hide the Resume button everywhere.
          void queryClient.invalidateQueries({ queryKey: currentSessionQueryKey });
        });
    }

    dispatch({ type: 'ENDED_CLEANUP_COMPLETE' });
  }, [
    state.phase,
    state.contacts,
    state.currentIndex,
    state.dialingState,
    abortPendingCallStart,
    t,
    queryClient,
    currentSessionQueryKey,
  ]);

  // ─── Effect: mute SDK ringback while our waiting music plays ───────────
  //
  // Vonage may emit remote early media/ringback through its own hidden audio
  // element while the outbound leg is still `ringing`. Power Dialer already
  // plays its branded waiting music below, so mute only the SDK output element
  // during that state. This does not mute the microphone, and it restores the
  // SDK element as soon as the call connects, hangs up, or the session ends.
  const sdkRingbackAudioRef = useRef<{ element: HTMLAudioElement; muted: boolean } | null>(null);

  const restoreSdkRingbackAudio = useCallback(() => {
    const snapshot = sdkRingbackAudioRef.current;
    if (!snapshot) {
      return;
    }
    snapshot.element.muted = snapshot.muted;
    sdkRingbackAudioRef.current = null;
  }, []);

  const muteSdkRingbackAudio = useCallback(() => {
    const audio = vonageClient.getAudioOutputElement();
    if (!audio) {
      return;
    }

    const current = sdkRingbackAudioRef.current;
    if (current?.element === audio) {
      audio.muted = true;
      return;
    }

    restoreSdkRingbackAudio();
    sdkRingbackAudioRef.current = { element: audio, muted: audio.muted };
    audio.muted = true;
  }, [vonageClient, restoreSdkRingbackAudio]);

  useEffect(() => {
    const isActive = state.phase === 'dialing' || state.phase === 'paused';
    const shouldMuteSdkRingback =
      isActive && !!state.inFlight && direction === 'outgoing' && callStatus === 'ringing';
    if (!shouldMuteSdkRingback) {
      restoreSdkRingbackAudio();
      return;
    }

    muteSdkRingbackAudio();
    const intervalId = window.setInterval(muteSdkRingbackAudio, 100);
    return () => {
      window.clearInterval(intervalId);
      restoreSdkRingbackAudio();
    };
  }, [
    state.phase,
    state.inFlight,
    direction,
    callStatus,
    muteSdkRingbackAudio,
    restoreSdkRingbackAudio,
  ]);

  // ─── Effect: custom ring sound while the outbound call is ringing ──────
  // Plays while `callStatus === 'ringing'` and stops the instant the SDK
  // transitions out — to 'in-progress' (callee picked up), 'hangingup',
  // 'idle', anything. Relies on the `legStatusUpdate` listener in
  // `use-server-call.ts` firing `LegStatus.Answered` reliably; that listener
  // was previously flapping due to bogus effect deps, which caused the music
  // to overlap live voice for back-to-back calls. Fixed upstream.
  const ringAudioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const isActive = state.phase === 'dialing' || state.phase === 'paused';
    const shouldPlay =
      isActive && !!state.inFlight && direction === 'outgoing' && callStatus === 'ringing';
    if (shouldPlay) {
      if (!ringAudioRef.current) {
        const audio = new Audio(powerDialerRingSound);
        audio.loop = true;
        audio.volume = 0.4;
        audio.play().catch(() => undefined);
        ringAudioRef.current = audio;
      }
      return;
    }
    const audio = ringAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      ringAudioRef.current = null;
    }
  }, [state.phase, state.inFlight, direction, callStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setPowerDialerBusyModeBannerHidden(false);
      if (ringAudioRef.current) {
        ringAudioRef.current.pause();
        ringAudioRef.current = null;
      }
    };
  }, []);

  const value = useMemo<ActivePowerDialerContextValue>(() => {
    const activeContact = state.contacts[state.currentIndex];
    return {
      activeSessionId: state.sessionId,
      activeContactId: activeContact ? getSessionContactKey(activeContact) : undefined,
      currentIndex: state.currentIndex,
      isPaused: state.phase === 'paused',
      stateOverrides: state.dialingState,
      outboundLine: state.line,
      startSession,
      claimSession,
      resumeSession,
      endSession,
      pause,
      resume,
      skip,
      goToIndex,
      state,
      lastEndedSession,
      clearLastEndedSession,
    };
  }, [
    state,
    startSession,
    claimSession,
    resumeSession,
    endSession,
    pause,
    resume,
    skip,
    goToIndex,
    lastEndedSession,
    clearLastEndedSession,
  ]);

  return (
    <ActivePowerDialerContext.Provider value={value}>{children}</ActivePowerDialerContext.Provider>
  );
};

export const useActivePowerDialer = () => {
  const ctx = useContext(ActivePowerDialerContext);
  if (!ctx) {
    throw new Error('useActivePowerDialer must be used within <ActivePowerDialerProvider>');
  }
  return ctx;
};

/** Safe variant that returns null when called outside the provider tree. */
export const useActivePowerDialerSafe = (): ActivePowerDialerContextValue | null => {
  return useContext(ActivePowerDialerContext);
};
